import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Environment, Lightformer } from "@react-three/drei";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader.js";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";

/**
 * CINEMATIC WEBGL INTRO — Phase 0 der Hero-Experience.
 *
 * Dunkler 3D-Raum → JUSTIN-Schriftzug als echtes 3D-Objekt (Extrusion, Bevels,
 * metallische Gold-/Silber-Materialien, Rim-Lights, Nebel) → Kamera-Dolly →
 * das Logo ZERLEGT sich wie eine Robotermechanik in echte Metall-Fragmente
 * (mit Tiefe, Fase, Seitenwand — keine 2D-Pixel) → die Teile fliegen durch den
 * Raum, bauen sich zu einem Ring-Tor zusammen → die Kamera fliegt hindurch →
 * das Tor schließt sich, alle Teile schrumpfen auf null (nichts bleibt zurück)
 * → Handoff an die bestehende 2D-Skizzen-Sequenz (DOM-Crossfade in HeroExperience).
 *
 * Alles scroll-gescrubbt (progress MotionValue), komplett reversibel.
 * Performance: frameloop="demand" — es wird nur gerendert, wenn gescrollt wird.
 * Keinerlei externe Assets (Font lokal, Environment = Lightformer, kein CDN).
 */

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const seg = (p, s, e) => clamp01((p - s) / (e - s));
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const lerp = (a, b, t) => a + (b - a) * t;

// Deterministischer Zufall (jedes Laden identisch — art-directed, nicht zufällig)
function mulberry32(seed) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const LETTERS = ["J", "U", "S", "T", "I", "N", "."];
const LETTER_SIZE = 1.55;
const LETTER_DEPTH = 0.42;
const LINE1_Y = 0.55;
// Zweite Zeile — der volle Markenname
const TAGLINE = "WEB DESIGN & CLOTHING";
const TAGLINE_SIZE = 0.46;
const TAGLINE_DEPTH = 0.14;
const LINE2_Y = -0.95;
// Kamera-Basisabstände (werden per Kamera-Fit für schmale Screens hochskaliert)
const CAM_FAR = 11.5;
const CAM_NEAR = 7.2;

// ---------------------------------------------------------------------------
// Fragmentierung: Buchstaben-Geometrie in ein Raster echter 3D-Platten
// zerschneiden. Jedes Dreieck wandert per Schwerpunkt in seine Zelle — die
// Fragmente behalten Vorderseite, Fase UND Seitenwand (Material-Gruppen),
// sind also echte Metallteile mit Tiefe, keine flachen Sprites.
// ---------------------------------------------------------------------------
function sliceLetter(geo, cols, rows) {
  const src = geo.index ? geo.toNonIndexed() : geo;
  const pos = src.getAttribute("position");
  const nor = src.getAttribute("normal");
  src.computeBoundingBox();
  const bb = src.boundingBox;
  const w = bb.max.x - bb.min.x || 1;
  const h = bb.max.y - bb.min.y || 1;
  const matIndexOf = (tri) => {
    for (const g of src.groups) if (tri >= g.start && tri < g.start + g.count) return g.materialIndex;
    return 0;
  };
  const cells = new Map();
  for (let i = 0; i < pos.count; i += 3) {
    const cx = (pos.getX(i) + pos.getX(i + 1) + pos.getX(i + 2)) / 3;
    const cy = (pos.getY(i) + pos.getY(i + 1) + pos.getY(i + 2)) / 3;
    const col = Math.min(cols - 1, Math.floor(((cx - bb.min.x) / w) * cols));
    const row = Math.min(rows - 1, Math.floor(((cy - bb.min.y) / h) * rows));
    const key = row * cols + col;
    if (!cells.has(key)) cells.set(key, { col, row, verts: [[], []], norms: [[], []] });
    const cell = cells.get(key);
    const m = matIndexOf(i) === 1 ? 1 : 0;
    for (let k = 0; k < 3; k++) {
      cell.verts[m].push(pos.getX(i + k), pos.getY(i + k), pos.getZ(i + k));
      cell.norms[m].push(nor.getX(i + k), nor.getY(i + k), nor.getZ(i + k));
    }
  }
  const fragments = [];
  cells.forEach((cell) => {
    const v = new Float32Array([...cell.verts[0], ...cell.verts[1]]);
    const n = new Float32Array([...cell.norms[0], ...cell.norms[1]]);
    if (v.length < 9) return;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(v, 3));
    g.setAttribute("normal", new THREE.BufferAttribute(n, 3));
    const n0 = cell.verts[0].length / 3;
    g.addGroup(0, n0, 0);
    g.addGroup(n0, cell.verts[1].length / 3, 1);
    g.computeBoundingBox();
    const center = new THREE.Vector3();
    g.boundingBox.getCenter(center);
    g.translate(-center.x, -center.y, -center.z);
    fragments.push({ geo: g, center, col: cell.col, row: cell.row, cols, rows });
  });
  return fragments;
}

// ---------------------------------------------------------------------------
// 3D-Logo als Robotermechanik: Buchstaben bestehen aus Metall-Fragmenten.
// Timeline (u = Intro-Fortschritt):
//   0.00–0.18  Logo intakt, Kamera-Dolly
//   0.18–0.34  UNLOCK: Platten lösen sich gestaffelt (Klick-Kippen, Spalt öffnet sich)
//   0.30–0.62  DISASSEMBLE: Teile fliegen mechanisch auseinander, rotieren im Raum
//   0.56–0.80  REASSEMBLE: Teile bauen sich zu einem Ring-Tor zusammen, die Kamera
//              fliegt hindurch
//   0.78–0.92  DISSOLVE: Tor schließt sich, Teile schrumpfen auf 0 — nichts bleibt zurück
// ---------------------------------------------------------------------------
function LogoMechanics({ progress, introEnd, faces, side, fitRef, isMobile }) {
  const [frags, setFrags] = useState(null);
  const meshRefs = useRef([]);
  const tmp = useMemo(() => ({ q: new THREE.Quaternion(), e: new THREE.Euler(), v: new THREE.Vector3() }), []);

  useEffect(() => {
    let cancelled = false;
    const rand = mulberry32(20260928);
    new FontLoader().loadAsync("/fonts/helvetiker_bold.typeface.json").then((font) => {
      if (cancelled) return;
      const all = [];
      let maxHalfW = 0;

      // Eine Textzeile in Buchstaben-Geometrien zerlegen, zentrieren, fragmentieren
      const buildLine = (text, size, depth, y, slice, faceIdxFor) => {
        const chars = text.split("");
        const geos = chars.map((ch) =>
          ch === " "
            ? null
            : new TextGeometry(ch, {
                font,
                size,
                depth,
                curveSegments: 10,
                bevelEnabled: true,
                bevelThickness: size * 0.03,
                bevelSize: size * 0.02,
                bevelSegments: 3,
              })
        );
        const track = size * 0.09;
        const widths = geos.map((g) => {
          if (!g) return size * 0.42; // Wortabstand
          g.computeBoundingBox();
          return g.boundingBox.max.x - g.boundingBox.min.x;
        });
        const total = widths.reduce((a, w) => a + w, 0) + track * (chars.length - 1);
        maxHalfW = Math.max(maxHalfW, total / 2);
        let cursor = -total / 2;
        geos.forEach((g, li) => {
          const x = cursor + widths[li] / 2;
          cursor += widths[li] + track;
          if (!g) return;
          g.center();
          const [cols, rows] = slice(widths[li], chars[li]);
          const pieces = sliceLetter(g, cols, rows);
          const tNorm = li / Math.max(1, chars.length - 1) - 0.5; // -0.5..0.5
          pieces.forEach((p) => {
            const origin = new THREE.Vector3(x + p.center.x, y + p.center.y, p.center.z);
            // Unlock: Platte kippt/rückt vom Buchstabenzentrum weg (+ nach vorn)
            const unlockDir = new THREE.Vector3(p.center.x, p.center.y, 0.6 + rand() * 0.5).normalize();
            // Disassemble: mechanisch, lateral + Z-Schub Richtung Kamera
            const flyDir = new THREE.Vector3(
              tNorm * 4.2 + (p.col - (p.cols - 1) / 2) * 0.9 + (rand() - 0.5) * 0.6,
              y * 0.6 + (p.row - (p.rows - 1) / 2) * 1.1 + (rand() - 0.5) * 0.8,
              0.6 + rand() * 1.1
            ).normalize();
            // Ring-Slot (Tor): gleichmäßig verteilt, leicht gestreut
            const slot = rand() * Math.PI * 2;
            all.push({
              geo: p.geo,
              matIdx: faceIdxFor(chars[li]),
              origin,
              unlockDir,
              flyDir,
              flyDist: 2.4 + rand() * 3.2,
              axis: new THREE.Vector3(rand() - 0.5, rand() - 0.5, rand() - 0.5).normalize(),
              spin: (1.2 + rand() * 2.6) * (rand() > 0.5 ? 1 : -1),
              stagger: (p.row / Math.max(1, p.rows - 1)) * 0.5 + rand() * 0.5, // 0..1
              ring: new THREE.Vector3(Math.cos(slot) * 2.3, Math.sin(slot) * 1.7, -5 + (rand() - 0.5) * 0.5),
              ringRot: slot,
            });
          });
        });
      };

      // Zeile 1: JUSTIN. — groß, Punkt in Gold, Buchstaben in 2–3 × 4 Platten
      buildLine(
        LETTERS.join(""),
        LETTER_SIZE,
        LETTER_DEPTH,
        LINE1_Y,
        (w, ch) => (ch === "." ? [1, 1] : isMobile ? [w > 1.1 ? 2 : 1, 2] : w > 1.1 ? [3, 4] : [2, 4]),
        (ch) => (ch === "." ? 1 : 0)
      );
      // Zeile 2: WEB DESIGN & CLOTHING — der volle Markenname, „&" in Gold,
      // jeder Buchstabe ein eigenes Mechanik-Teil
      buildLine(TAGLINE, TAGLINE_SIZE, TAGLINE_DEPTH, LINE2_Y, () => [1, 1], (ch) => (ch === "&" ? 1 : 0));

      // Kamera-Fit: gesamte Wortmarke muss in JEDEM Seitenverhältnis sichtbar sein
      fitRef.current = maxHalfW * 1.12;
      setFrags(all);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFrame(() => {
    if (!frags) return;
    const u = clamp01(progress.get() / introEnd);
    const dissolve = seg(u, 0.78, 0.92);
    const alive = dissolve < 1;
    // Materialien bleiben opak (echte Metallteile) — sie SCHRUMPFEN statt zu verblassen
    frags.forEach((d, i) => {
      const mesh = meshRefs.current[i];
      if (!mesh) return;
      if (!alive) {
        mesh.visible = false;
        return;
      }
      mesh.visible = true;
      const st = d.stagger * 0.08;
      // Phase 1 — Unlock (gestaffelt, mechanisches "Klick": schneller Start, harter Stopp)
      const unlock = easeOut(seg(u, 0.18 + st, 0.3 + st));
      // Phase 2 — Disassemble
      const fly = easeInOut(seg(u, 0.3 + st, 0.6 + st));
      // Phase 3 — Reassemble zum Ring-Tor (steht, bevor die Kamera hindurchfliegt)
      const gate = easeInOut(seg(u, 0.5 + st * 0.5, 0.68));
      // Phase 4 — Dissolve (Ring schließt sich, Teile schrumpfen)
      const shrink = 1 - easeInOut(dissolve);

      // Position: Origin → Unlock-Spalt → Flugbahn → Ring-Slot
      tmp.v.copy(d.origin).addScaledVector(d.unlockDir, unlock * 0.22).addScaledVector(d.flyDir, fly * d.flyDist);
      mesh.position.lerpVectors(tmp.v, d.ring, gate);
      // Tor schließt sich beim Auflösen zur Mitte
      mesh.position.x *= 1 - dissolve * 0.35 * gate;
      mesh.position.y *= 1 - dissolve * 0.35 * gate;

      // Rotation: Kipp-Klick → freie Rotation → tangential im Ring ausgerichtet
      const spin = unlock * 0.18 + fly * d.spin;
      tmp.e.set(d.axis.x * spin, d.axis.y * spin, d.axis.z * spin);
      mesh.quaternion.setFromEuler(tmp.e);
      tmp.e.set(0, 0, d.ringRot + Math.PI / 2);
      mesh.quaternion.slerp(tmp.q.setFromEuler(tmp.e), gate);

      mesh.scale.setScalar((1 - 0.08 * fly) * shrink);
    });
  });

  if (!frags) return null;
  return (
    <group>
      {frags.map((d, i) => (
        <mesh
          key={i}
          ref={(el) => (meshRefs.current[i] = el)}
          geometry={d.geo}
          material={[faces[d.matIdx], side]}
          position={d.origin}
        />
      ))}
    </group>
  );
}

// ---------------------------------------------------------------------------
// Kamera: Dolly → Durchflug — komplett scroll-gescrubbt.
// Kamera-Fit: Der Abstand skaliert mit dem Seitenverhältnis, sodass die volle
// Wortmarke (beide Zeilen) auf Desktop, 16:9 UND Hochkant-Handy sichtbar bleibt.
// ---------------------------------------------------------------------------
function CameraRig({ progress, introEnd, fitRef }) {
  const lookTarget = useMemo(() => new THREE.Vector3(), []);
  const aspect = useThree((s) => s.viewport.aspect);
  useFrame(({ camera, scene }) => {
    const u = clamp01(progress.get() / introEnd);
    // Nötiger Abstand, damit halfW (Wortmarke) in die halbe Bildbreite passt
    const halfTan = Math.tan((camera.fov * Math.PI) / 360);
    const needed = fitRef.current / (halfTan * Math.max(aspect, 0.3));
    const k = Math.max(1, needed / CAM_NEAR);
    // Nebel mit dem Abstand zurücknehmen — die Wortmarke bleibt auch weit weg klar
    if (scene.fog) scene.fog.density = 0.02 / k;
    const zFar = CAM_FAR * k;
    const zNear = CAM_NEAR * k;
    const z =
      u < 0.18
        ? lerp(zFar, zNear, easeInOut(u / 0.18))
        : u < 0.5
          ? lerp(zNear, 4.2, easeInOut(seg(u, 0.18, 0.5)))
          : u < 0.85
            ? lerp(4.2, -6, easeInOut(seg(u, 0.5, 0.85)))
            : lerp(-6, -8.5, seg(u, 0.85, 1));
    const x = u < 0.3 ? lerp(0, 0.55, seg(u, 0, 0.3)) : lerp(0.55, 0, seg(u, 0.3, 0.6));
    const y = -0.15 + seg(u, 0, 0.3) * 0.3 - seg(u, 0.3, 0.7) * 0.3;
    camera.position.set(x, y, z);
    // Beim Durchflug schwenkt der Blick vom Logo auf die Flugbahn
    const ahead = seg(u, 0.45, 0.65);
    lookTarget.set(0, lerp(-0.2, 0, ahead), lerp(0, camera.position.z - 10, ahead));
    camera.lookAt(lookTarget);
    camera.rotation.z += Math.sin(u * Math.PI) * 0.045; // cineastischer Roll
  });
  return null;
}

// Demand-Frameloop: rAF-Poller vergleicht den ECHTEN Scroll-Wert pro Frame —
// robuster als change-Events (Event-Timing kann den letzten Frame verschlucken).
// Nach Ende der Intro schläft der Poller komplett ein (kein Dauer-rAF) und
// wacht über einen passiven Scroll-Listener wieder auf, sobald man hochscrollt.
function ScrollInvalidator({ progress, introEnd }) {
  const invalidate = useThree((s) => s.invalidate);
  useEffect(() => {
    let raf = null;
    let last = -1;
    let pastEndFrames = 0;
    let sleeping = false;
    const poll = () => {
      const v = progress.get();
      if (v !== last) {
        last = v;
        pastEndFrames = 0;
        invalidate();
      } else if (v > introEnd + 0.03) {
        pastEndFrames += 1;
        if (pastEndFrames >= 90) {
          sleeping = true;
          raf = null;
          return; // schlafen — Listener unten weckt bei Rückkehr
        }
      }
      raf = requestAnimationFrame(poll);
    };
    const wake = () => {
      if (sleeping && progress.get() <= introEnd + 0.03) {
        sleeping = false;
        pastEndFrames = 0;
        if (raf === null) raf = requestAnimationFrame(poll);
      }
    };
    raf = requestAnimationFrame(poll);
    window.addEventListener("scroll", wake, { passive: true });
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", wake);
    };
  }, [progress, invalidate, introEnd]);
  useEffect(() => {
    // initiale Frames (Font lädt asynchron nach)
    const iv = setInterval(invalidate, 400);
    const to = setTimeout(() => clearInterval(iv), 2400);
    return () => {
      clearInterval(iv);
      clearTimeout(to);
    };
  }, [invalidate]);
  return null;
}

export default function CinematicCanvas({ progress, introEnd }) {
  const isMobile =
    typeof window !== "undefined" &&
    (window.innerWidth < 768 || !window.matchMedia("(pointer: fine)").matches);
  // Halbe Breite der Wortmarke (wird nach dem Font-Load gemessen) → Kamera-Fit
  const fitRef = useRef(4.6);

  // Physische Materialien: Gesichter Silber-Offwhite / Punkt Gold, Seiten Graphit
  const materials = useMemo(() => {
    const faceSilver = new THREE.MeshPhysicalMaterial({
      color: "#e9e6df",
      metalness: 0.92,
      roughness: 0.24,
      envMapIntensity: 1.4,
    });
    const faceGold = new THREE.MeshPhysicalMaterial({
      color: "#d4af37",
      metalness: 1,
      roughness: 0.18,
      envMapIntensity: 1.6,
    });
    const side = new THREE.MeshPhysicalMaterial({
      color: "#16130a",
      metalness: 0.7,
      roughness: 0.5,
      envMapIntensity: 0.8,
    });
    return { faces: [faceSilver, faceGold], side };
  }, []);

  return (
    <Canvas
      data-testid="cinematic-canvas"
      frameloop="demand"
      dpr={isMobile ? [1, 1.25] : [1, 1.8]}
      gl={{ antialias: !isMobile, alpha: true, powerPreference: "high-performance", preserveDrawingBuffer: true }}
      camera={{ fov: 42, position: [0, 0.05, 11.5], near: 0.1, far: 70 }}
      style={{ position: "absolute", inset: 0 }}
    >
      <fogExp2 attach="fog" args={["#050505", 0.02]} />
      {/* Cineastisches Licht: Ambient + warmer Key + Front-Fill + goldene/silberne Rims */}
      <ambientLight intensity={0.28} />
      <spotLight position={[4, 6, 7]} intensity={85} angle={0.5} penumbra={0.8} color="#fff2dc" />
      <pointLight position={[0, 0.6, 9]} intensity={14} color="#ffedd0" />
      <spotLight position={[-7, 2.5, -3]} intensity={110} angle={0.6} penumbra={1} color="#d4af37" />
      <spotLight position={[7, -3.5, -4]} intensity={55} angle={0.6} penumbra={1} color="#b8bcc4" />
      {/* Studio-Umgebung für Metall-Reflexionen — rein prozedural, kein CDN */}
      <Environment resolution={128}>
        <Lightformer intensity={2.2} position={[0, 5, 4]} scale={[12, 5, 1]} color="#fff6e6" />
        <Lightformer intensity={3} position={[-6, 0.5, -1]} rotation-y={Math.PI / 2} scale={[9, 2.2, 1]} color="#d4af37" />
        <Lightformer intensity={1.6} position={[6, -0.5, -1]} rotation-y={-Math.PI / 2} scale={[9, 2.2, 1]} color="#c7cad1" />
      </Environment>

      <ScrollInvalidator progress={progress} introEnd={introEnd} />
      <CameraRig progress={progress} introEnd={introEnd} fitRef={fitRef} />
      <LogoMechanics progress={progress} introEnd={introEnd} faces={materials.faces} side={materials.side} fitRef={fitRef} isMobile={isMobile} />
    </Canvas>
  );
}
